import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import { DEFAULT_ACCENT_COLOR } from "@/lib/brand-colors";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { BOOKING_REQUEST_FIELD_TYPES, makePadPulseDefaultFields, PADPULSE_DEFAULT_TEMPLATE_NAME, PADPULSE_DEFAULT_TEMPLATE_DESCRIPTION, type BookingRequestField, type BookingRequestFieldType, type BookingRequestTemplate, type BookingRequestSubmission, type BookingRequestStatus, type FieldCondition, type MessagingRule } from "@shared/schema";
import { computeVisibleFieldIds, findStaleConditionFields, findUnreachablePages } from "@shared/booking-request-visibility";
import { BOOKING_REJECTION_CATEGORIES, bookingRejectionCategoryLabel } from "@shared/booking-rejection-categories";
import { BookingRequestsStatsTab, type StatsDrillThrough } from "@/pages/app-members-booking-requests-stats";
import { computeDateRange } from "@/components/DateRangeFilter";
import { PhotoLightbox, PdfLightbox, downloadFileNameFor } from "@/components/message-attachments";
import { usePropertyGroups } from "@/components/property-group-select";
import { FileText, File as FileIcon, Download, Archive } from "lucide-react";
import { appImagePreviewUrl } from "@/pages/app-members-booking-requests-settings";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Settings, Copy, GripVertical, Check, X, ExternalLink, Files, Eye, Sparkles, Upload, User, Loader2, GitBranch, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, Info } from "lucide-react";
import { PropertyMultiSelectFilter } from "@/components/property-multi-select-filter";
import { TextToolbar, renderMarkdown, renderInline, FormattingHelp } from "@/lib/booking-form-markdown";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type Property = { id: number; name?: string | null; address?: string | null; status?: string | null; propertyGroup?: string | null };

const FIELD_TYPE_LABELS: Record<string, string> = {
  text: "Short text", longtext: "Long text", email: "Email", phone: "Phone",
  number: "Number", date: "Date",
  yesno: "Yes / No", radio: "Single choice (radio)", select: "Single select (dropdown)", multiselect: "Multi select",
  photo: "Photo (camera)",
  video_record: "Video — record",
  video_upload: "Video — upload",
  signature: "Signature",
  agreement: "Signed agreement", file: "File upload",
};

function makeId() {
  return `f_${Math.random().toString(36).slice(2, 10)}`;
}

// Collapse a (possibly multi-line) question label into a single line for
// compact summary surfaces (condition summaries, stale warnings, selects).
function singleLineLabel(label: string | null | undefined): string {
  return String(label ?? "").replace(/\s+/g, " ").trim();
}

function newField(type: BookingRequestFieldType): BookingRequestField {
  const isVideo = type === "video_record" || type === "video_upload";
  const hasOptions = type === "select" || type === "multiselect" || type === "radio";
  return {
    id: `f_${Math.random().toString(36).slice(2, 10)}`,
    type,
    label: FIELD_TYPE_LABELS[type] || "Field",
    required: false,
    helpText: "",
    placeholder: "",
    options: hasOptions ? ["Option 1", "Option 2"] : null,
    agreementText: type === "agreement" ? "I agree to the terms and conditions." : null,
    maxFiles: type === "photo" ? 6 : isVideo ? 1 : type === "file" ? 3 : null,
    maxDurationSeconds: isVideo ? 60 : null,
  };
}

const pageOf = (f: BookingRequestField) => f.page ?? 1;

function maxPageOf(fields: BookingRequestField[]): number {
  return fields.reduce((m, f) => Math.max(m, pageOf(f)), 1);
}

/** Stable sort that groups fields by page so array order always matches page order. */
function resortByPage(fields: BookingRequestField[]): BookingRequestField[] {
  return fields
    .map((f, i) => ({ f, i }))
    .sort((a, b) => pageOf(a.f) - pageOf(b.f) || a.i - b.i)
    .map((x) => x.f);
}

const CHOICE_FIELD_TYPES = new Set(["yesno", "radio", "select", "multiselect"]);

function ConditionsEditor({
  field, fieldIndex, allFields, onChange,
}: {
  field: BookingRequestField;
  fieldIndex: number;
  allFields: BookingRequestField[];
  onChange: (updates: Partial<BookingRequestField>) => void;
}) {
  const eligibleSources = allFields.slice(0, fieldIndex);
  const conditions = field.conditions ?? [];
  const combinator = field.conditionCombinator ?? "all";

  function getDefaultValues(src: BookingRequestField): (string | boolean | number)[] {
    if (src.type === "yesno") return [true];
    if (src.options?.length) return [src.options[0]];
    return [""];
  }

  function addCondition() {
    const first = eligibleSources[0];
    if (!first) return;
    onChange({
      conditions: [...conditions, { sourceFieldId: first.id, operator: "equals", values: getDefaultValues(first) }],
    });
  }

  function removeCondition(ci: number) {
    const next = conditions.filter((_, i) => i !== ci);
    onChange({ conditions: next.length ? next : null, conditionCombinator: next.length ? combinator : null });
  }

  function updateCond(ci: number, patch: Partial<FieldCondition>) {
    onChange({ conditions: conditions.map((c, i) => i === ci ? { ...c, ...patch } : c) });
  }

  const eligibleSet = new Set(eligibleSources.map((s) => s.id));

  return (
    <div className="col-span-2 rounded border bg-blue-50/50 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700">Show only if…</span>
        <Button type="button" size="sm" variant="outline" className="h-6 text-xs px-2" onClick={addCondition} disabled={eligibleSources.length === 0} data-testid={`button-add-condition-${fieldIndex}`}>
          <Plus className="h-3 w-3 mr-1" />Add condition
        </Button>
      </div>

      {eligibleSources.length === 0 && conditions.length === 0 && (
        <p className="text-xs text-gray-400">No earlier questions to use as conditions. Move this field further down to unlock.</p>
      )}

      {conditions.map((cond, ci) => {
        const srcField = allFields.find((f) => f.id === cond.sourceFieldId);
        const isStale = !eligibleSet.has(cond.sourceFieldId);
        const hasOptions = srcField && CHOICE_FIELD_TYPES.has(srcField.type);
        return (
          <div key={ci} className="border rounded bg-white p-2 space-y-2">
            {isStale && (
              <p className="text-xs text-amber-600 font-medium">⚠ This referenced question no longer appears before this one.</p>
            )}
            <div className="flex items-start gap-2">
              <div className="flex-1 space-y-2 min-w-0">
                <div className="flex gap-2 flex-wrap">
                  <select
                    className="border rounded text-xs px-2 py-1 flex-1 min-w-0"
                    value={cond.sourceFieldId}
                    onChange={(e) => {
                      const newSrc = allFields.find((f) => f.id === e.target.value);
                      updateCond(ci, { sourceFieldId: e.target.value, operator: "equals", values: newSrc ? getDefaultValues(newSrc) : [""] });
                    }}
                    data-testid={`select-cond-source-${fieldIndex}-${ci}`}
                  >
                    {eligibleSources.map((s) => <option key={s.id} value={s.id}>{singleLineLabel(s.label)}</option>)}
                    {isStale && <option value={cond.sourceFieldId}>[deleted question]</option>}
                  </select>
                  <select
                    className="border rounded text-xs px-2 py-1"
                    value={cond.operator}
                    onChange={(e) => updateCond(ci, { operator: e.target.value as FieldCondition["operator"] })}
                    data-testid={`select-cond-op-${fieldIndex}-${ci}`}
                  >
                    <option value="equals">is</option>
                    <option value="not-equals">is not</option>
                    {srcField && srcField.type !== "yesno" && <option value="is-any-of">is any of</option>}
                  </select>
                </div>

                {srcField?.type === "yesno" && (
                  <div className="flex gap-3">
                    {([true, false] as const).map((val) => (
                      <label key={String(val)} className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input
                          type="radio"
                          name={`cond-yn-${field.id}-${ci}`}
                          checked={cond.values[0] === val}
                          onChange={() => updateCond(ci, { values: [val] })}
                          className="h-3.5 w-3.5"
                        />
                        {val ? "Yes" : "No"}
                      </label>
                    ))}
                  </div>
                )}

                {hasOptions && srcField.type !== "yesno" && cond.operator === "is-any-of" && (
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {(srcField.options || []).map((opt) => (
                      <label key={opt} className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <Checkbox
                          checked={cond.values.includes(opt)}
                          onCheckedChange={(checked) => {
                            const next = checked ? [...cond.values, opt] : cond.values.filter((v) => v !== opt);
                            updateCond(ci, { values: next.length ? next : [opt] });
                          }}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                )}

                {hasOptions && srcField.type !== "yesno" && cond.operator !== "is-any-of" && (
                  <select
                    className="border rounded text-xs px-2 py-1 w-full"
                    value={String(cond.values[0] ?? "")}
                    onChange={(e) => updateCond(ci, { values: [e.target.value] })}
                    data-testid={`select-cond-val-${fieldIndex}-${ci}`}
                  >
                    {(srcField.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                )}

                {!hasOptions && !isStale && (
                  <Input
                    className="text-xs h-7"
                    value={String(cond.values[0] ?? "")}
                    onChange={(e) => updateCond(ci, { values: [e.target.value] })}
                    placeholder="Value to match"
                    data-testid={`input-cond-val-${fieldIndex}-${ci}`}
                  />
                )}
              </div>
              <Button type="button" size="icon" variant="ghost" className="h-7 w-7 flex-shrink-0" onClick={() => removeCondition(ci)} data-testid={`button-remove-cond-${fieldIndex}-${ci}`}>
                <Trash2 className="h-3.5 w-3.5 text-red-500" />
              </Button>
            </div>
          </div>
        );
      })}

      {conditions.length > 1 && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-500">Require</span>
          <select
            className="border rounded text-xs px-2 py-1"
            value={combinator}
            onChange={(e) => onChange({ conditionCombinator: e.target.value as "all" | "any" })}
            data-testid={`select-cond-combinator-${fieldIndex}`}
          >
            <option value="all">all conditions (AND)</option>
            <option value="any">any condition (OR)</option>
          </select>
          <span className="text-gray-500">to be met</span>
        </div>
      )}

      {conditions.length > 0 && (
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Checkbox
            checked={field.requiredWhenVisible === true}
            onCheckedChange={(v) => onChange({ requiredWhenVisible: v === true })}
            data-testid={`checkbox-required-when-visible-${fieldIndex}`}
          />
          Required when this question is visible
        </label>
      )}
    </div>
  );
}

const BRANDING_FIELD_LABELS: Record<string, string> = {
  brandName: "Brand name",
  brandLogoUrl: "Brand logo",
  accentColor: "Accent color",
  headline: "Headline",
  description: "Description",
  contactPhone: "Contact phone",
  contactEmail: "Contact email",
  ctaButtonText: "CTA button text",
  footerText: "Footer text",
};

export function TemplateEditor({ open, onOpenChange, template }: { open: boolean; onOpenChange: (v: boolean) => void; template: BookingRequestTemplate | null }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [name, setName] = useState(template?.name || "");
  const [description, setDescription] = useState(template?.description || "");
  const [fields, setFields] = useState<BookingRequestField[]>(((template?.fields as BookingRequestField[]) || []));
  const [pageCount, setPageCount] = useState<number>(maxPageOf((template?.fields as BookingRequestField[]) || []));
  const [propertyIds] = useState<number[]>((template?.propertyIds as number[]) || []);
  const [isActive, setIsActive] = useState(template?.isActive ?? true);
  const [brandingOverride, setBrandingOverride] = useState<Record<string, string>>(((template?.brandingOverride as Record<string, string>) || {}));
  const [overrideEnabled, setOverrideEnabled] = useState<boolean>(!!template?.brandingOverride);
  const [loadDefaultOpen, setLoadDefaultOpen] = useState(false);
  const [staleWarning, setStaleWarning] = useState<string[] | null>(null);
  const [unreachablePageWarning, setUnreachablePageWarning] = useState<number[] | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const overrideDescRef = useRef<HTMLTextAreaElement | null>(null);
  const autoExpandedTemplateKeyRef = useRef<number | null | undefined>(undefined);

  function wrapOverrideDescription(prefix: string, suffix: string = prefix) {
    const ta = overrideDescRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const value = brandingOverride.description || "";
    const selected = value.slice(start, end);
    const next = value.slice(0, start) + prefix + selected + suffix + value.slice(end);
    setBrandingOverride({ ...brandingOverride, description: next });
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + prefix.length, end + prefix.length); }, 0);
  }

  function insertOverrideListPrefix(marker: string) {
    const ta = overrideDescRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const value: string = brandingOverride.description || "";
    const before = value.slice(0, start);
    const insert = (before.endsWith("\n") || start === 0 ? "" : "\n") + marker;
    const next = before + insert + value.slice(start);
    setBrandingOverride({ ...brandingOverride, description: next });
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + insert.length, start + insert.length); }, 0);
  }

  const insertOverrideBullets = () => insertOverrideListPrefix("- ");
  const insertOverrideNumbered = () => insertOverrideListPrefix("1. ");

  // --- Per-field text-toolbar helpers (labels & agreement text) ---
  function getFieldTextarea(fieldId: string): HTMLTextAreaElement | null {
    return document.getElementById(`textarea-field-label-${fieldId}`) as HTMLTextAreaElement | null;
  }

  function getAgreementTextarea(fieldId: string): HTMLTextAreaElement | null {
    return document.getElementById(`textarea-agreement-${fieldId}`) as HTMLTextAreaElement | null;
  }

  function wrapFieldText(idx: number, prefix: string, suffix: string = prefix) {
    const f = fields[idx];
    const ta = getFieldTextarea(f.id);
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const value = f.label;
    const selected = value.slice(start, end);
    const next = value.slice(0, start) + prefix + selected + suffix + value.slice(end);
    setFields(fields.map((x, i) => (i === idx ? { ...x, label: next } : x)));
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + prefix.length, end + prefix.length); }, 0);
  }

  function insertFieldListPrefix(idx: number, marker: string) {
    const f = fields[idx];
    const ta = getFieldTextarea(f.id);
    if (!ta) return;
    const start = ta.selectionStart;
    const value: string = f.label;
    const before = value.slice(0, start);
    const insert = (before.endsWith("\n") || start === 0 ? "" : "\n") + marker;
    const next = before + insert + value.slice(start);
    setFields(fields.map((x, i) => (i === idx ? { ...x, label: next } : x)));
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + insert.length, start + insert.length); }, 0);
  }

  function wrapAgreementText(idx: number, prefix: string, suffix: string = prefix) {
    const f = fields[idx];
    const ta = getAgreementTextarea(f.id);
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const value = f.agreementText || "";
    const selected = value.slice(start, end);
    const next = value.slice(0, start) + prefix + selected + suffix + value.slice(end);
    setFields(fields.map((x, i) => (i === idx ? { ...x, agreementText: next } : x)));
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + prefix.length, end + prefix.length); }, 0);
  }

  function insertAgreementListPrefix(idx: number, marker: string) {
    const f = fields[idx];
    const ta = getAgreementTextarea(f.id);
    if (!ta) return;
    const start = ta.selectionStart;
    const value: string = f.agreementText || "";
    const before = value.slice(0, start);
    const insert = (before.endsWith("\n") || start === 0 ? "" : "\n") + marker;
    const next = before + insert + value.slice(start);
    setFields(fields.map((x, i) => (i === idx ? { ...x, agreementText: next } : x)));
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + insert.length, start + insert.length); }, 0);
  }

  type SettingsResponse = { settings: Record<string, any>; hasCustomSettings: boolean };
  const settingsQuery = useQuery<SettingsResponse>({ queryKey: ["/api/app/booking-requests/settings"], enabled: open });
  const inheritedDefaults = settingsQuery.data?.settings ?? {};

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) { toast({ title: "Only PNG, JPG, WebP, or GIF images are allowed", variant: "destructive" }); return; }
    if (file.size > 5 * 1024 * 1024) { toast({ title: "Logo must be under 5MB", variant: "destructive" }); return; }
    setUploadingLogo(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await apiRequest("POST", "/api/app/upload", { filename: file.name, mimeType: file.type, data: base64 });
      const json = await res.json();
      const publicUrl = json.imageUrl.replace("/api/app/images/", "/api/public/images/");
      setBrandingOverride((prev) => ({ ...prev, brandLogoUrl: publicUrl }));
      toast({ title: "Logo uploaded" });
    } catch {
      toast({ title: "Failed to upload logo", variant: "destructive" });
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  }

  const [optionDrafts, setOptionDrafts] = useState<Record<string, string>>({});

  function seedOptionDrafts(fs: BookingRequestField[]) {
    const drafts: Record<string, string> = {};
    for (const f of fs) {
      if (f.options) drafts[f.id] = f.options.join("\n");
    }
    setOptionDrafts(drafts);
  }

  function commitOptionDraft(fieldId: string) {
    const raw = optionDrafts[fieldId] ?? "";
    const normalized = raw.split("\n").map((s) => s.trim()).filter(Boolean);
    setFields((prev) => prev.map((x) => x.id === fieldId ? { ...x, options: normalized } : x));
  }

  function fieldsWithNormalizedOptions() {
    return fields.map((f) => {
      if (f.type !== "select" && f.type !== "multiselect" && f.type !== "radio") return f;
      const raw = optionDrafts[f.id] ?? (f.options || []).join("\n");
      return { ...f, options: raw.split("\n").map((s) => s.trim()).filter(Boolean) };
    });
  }

  function applyPadPulseDefault() {
    const defaults = makePadPulseDefaultFields(makeId);
    setFields(defaults);
    setPageCount(maxPageOf(defaults));
    seedOptionDrafts(defaults);
    if (!name) setName(PADPULSE_DEFAULT_TEMPLATE_NAME);
    if (!description) setDescription(PADPULSE_DEFAULT_TEMPLATE_DESCRIPTION);
    toast({ title: "PadPulse Default loaded", description: "13 fields added. Edit, reorder, or remove any field as needed." });
  }

  useEffect(() => {
    if (open) {
      setName(template?.name || "");
      setDescription(template?.description || "");
      const fs = (template?.fields as BookingRequestField[]) || [];
      setFields(fs);
      setPageCount(maxPageOf(fs));
      seedOptionDrafts(fs);
      setIsActive(template?.isActive ?? true);
      const bo = (template?.brandingOverride as Record<string, string>) || {};
      setBrandingOverride(bo);
      setOverrideEnabled(!!template?.brandingOverride);
      // Only auto-expand conditions panels the first time the editor opens for a
      // given template (or when it switches to a different template). On a repeat
      // open of the same template, preserve whatever expand/collapse state the
      // host left behind so the condition summary badges stay visible.
      const templateKey = template?.id ?? null;
      if (autoExpandedTemplateKeyRef.current !== templateKey) {
        const autoExpand = new Set<string>(fs.filter((f) => (f.conditions?.length ?? 0) > 0).map((f) => f.id));
        setConditionsExpanded(autoExpand);
        autoExpandedTemplateKeyRef.current = templateKey;
      }
    }
  }, [open, template]);

  const save = useMutation({
    mutationFn: async (overrideFields?: BookingRequestField[]) => {
      const body: Record<string, unknown> = { name, description, fields: overrideFields ?? fieldsWithNormalizedOptions(), propertyIds, isActive, brandingOverride: overrideEnabled ? brandingOverride : null };
      if (template) return apiRequest("PATCH", `/api/app/booking-requests/templates/${template.id}`, body);
      return apiRequest("POST", `/api/app/booking-requests/templates`, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/booking-requests/templates"] });
      toast({ title: template ? "Template updated" : "Template created" });
      onOpenChange(false);
    },
    onError: (e: any) => toast({ title: "Error", description: parseErrorMessage(e, "Couldn't save this template. Please try again."), variant: "destructive" }),
  });

  // Remove conditions whose source question was deleted or reordered to no
  // longer precede the dependent field. Returns the cleaned list plus the
  // labels of the affected fields so the host gets a summary of what changed.
  function stripStaleConditions(fs: BookingRequestField[]): { fields: BookingRequestField[]; affected: string[] } {
    const stale = findStaleConditionFields(fs);
    if (stale.length === 0) return { fields: fs, affected: [] };
    const staleIds = new Set(stale.map((s) => s.field.id));
    const cleaned = fs.map((f, idx) => {
      if (!staleIds.has(f.id)) return f;
      const priorIds = new Set(fs.slice(0, idx).map((p) => p.id));
      const kept = (f.conditions ?? []).filter((c) => priorIds.has(c.sourceFieldId));
      return { ...f, conditions: kept.length ? kept : null, conditionCombinator: kept.length ? f.conditionCombinator : null };
    });
    return { fields: cleaned, affected: stale.map((s) => singleLineLabel(s.field.label) || "Untitled question") };
  }

  // Gate the save on stale conditional logic: warn the host before broken
  // branching gets baked into the saved form (the backend rejects it too).
  function attemptSave() {
    const normalized = fieldsWithNormalizedOptions();
    const affected = findStaleConditionFields(normalized).map((s) => singleLineLabel(s.field.label) || "Untitled question");
    if (affected.length > 0) { setStaleWarning(affected); return; }
    // Page-level backstop: block saving a multi-page template that has a page
    // applicants can never see (every field permanently hidden). The backend
    // rejects the same case, so there's no "save anyway" — the host must fix it.
    const unreachable = findUnreachablePages(normalized);
    if (unreachable.length > 0) { setUnreachablePageWarning(unreachable); return; }
    save.mutate(undefined);
  }

  function removeStaleAndSave() {
    const normalized = fieldsWithNormalizedOptions();
    const { fields: cleaned } = stripStaleConditions(normalized);
    setFields(cleaned);
    setStaleWarning(null);
    // Removing stale conditions may leave a page with no reachable field, so
    // re-run the page backstop before persisting rather than saving blindly.
    const unreachable = findUnreachablePages(cleaned);
    if (unreachable.length > 0) { setUnreachablePageWarning(unreachable); return; }
    save.mutate(cleaned);
  }

  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const onDragStart = (idx: number) => (e: React.DragEvent) => { setDragIdx(idx); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (idx: number) => (e: React.DragEvent) => { e.preventDefault(); setDragOver(idx); };
  const onDragLeave = () => setDragOver(null);
  const onDrop = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOver(null); return; }
    const next = [...fields];
    const [moved] = next.splice(dragIdx, 1);
    // A field keeps its page; reorder within the destination card's page.
    moved.page = pageOf(fields[idx]);
    next.splice(idx, 0, moved);
    setFields(resortByPage(next));
    setDragIdx(null);
    setDragOver(null);
  };

  function moveFieldToPage(idx: number, newPage: number) {
    setFields((prev) => resortByPage(prev.map((x, i) => (i === idx ? { ...x, page: newPage } : x))));
  }

  function addPage() {
    setPageCount((n) => n + 1);
  }

  function deletePage(pageNum: number) {
    setFields((prev) =>
      resortByPage(
        prev.map((f) => {
          const p = pageOf(f);
          if (p === pageNum) return { ...f, page: Math.max(1, pageNum - 1) };
          if (p > pageNum) return { ...f, page: p - 1 };
          return f;
        }),
      ),
    );
    setPageCount((n) => Math.max(1, n - 1));
  }

  const [showPreview, setShowPreview] = useState(false);
  const [conditionsExpanded, setConditionsExpanded] = useState<Set<string>>(new Set());

  function toggleConditions(fieldId: string) {
    setConditionsExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) next.delete(fieldId);
      else next.add(fieldId);
      return next;
    });
  }

  // Live (per-keystroke/per-edit) detection of broken show/hide rules: as soon
  // as a field is deleted or reordered such that another field's condition no
  // longer references an earlier field, surface an inline amber warning on the
  // affected card — don't wait for the save-time guard. Reuses the same
  // findStaleConditionFields helper the save guard and backend validation use.
  // Keyed by field id → the stale source ids it points at, so the warning can
  // name which earlier question the broken rule should reference.
  const staleConditionSourcesById = new Map(
    findStaleConditionFields(fields).map((s) => [s.field.id, s.staleSourceIds]),
  );

  // Page-level companion to the per-card stale warning above: a multi-page
  // template can have a page whose *every* field is permanently hidden (each
  // field's show/hide rule references a deleted/later question or sits on an
  // unreachable branch), so applicants never see the page. Surface an inline
  // amber warning on that page's header immediately while editing, reusing the
  // shared visibility engine's reachability rule (findUnreachablePages).
  const unreachablePageNumbers = new Set(findUnreachablePages(fields));

  // Remove only the broken conditions from one field (those whose source no
  // longer precedes it), preserving any still-valid conditions on that field.
  function removeStaleConditionsFromField(idx: number) {
    setFields((prev) =>
      prev.map((f, i) => {
        if (i !== idx) return f;
        const priorIds = new Set(prev.slice(0, idx).map((p) => p.id));
        const kept = (f.conditions ?? []).filter((c) => priorIds.has(c.sourceFieldId));
        return {
          ...f,
          conditions: kept.length ? kept : null,
          conditionCombinator: kept.length ? f.conditionCombinator : null,
        };
      }),
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && uploadingLogo) return; onOpenChange(v); }}>
      <DialogContent
        className={`${showPreview ? "max-w-6xl" : "max-w-3xl"} max-h-[90vh] overflow-y-auto`}
        onInteractOutside={(e) => { if (uploadingLogo) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (uploadingLogo) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (uploadingLogo) e.preventDefault(); }}
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{template ? "Edit template" : "New booking request template"}</DialogTitle>
            <Button type="button" variant="outline" size="sm" onClick={() => setShowPreview((v) => !v)} data-testid="button-toggle-preview">
              <Eye className="h-4 w-4 mr-1" />{showPreview ? "Hide preview" : "Show preview"}
            </Button>
          </div>
        </DialogHeader>

        <div className={showPreview ? "grid grid-cols-2 gap-6" : ""}>
        <div className="space-y-5">
          <div>
            <Label>Template name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Prospect intake" data-testid="input-template-name" />
          </div>
          <div>
            <Label>Description (internal)</Label>
            <Textarea value={description || ""} onChange={(e) => setDescription(e.target.value)} placeholder="Used for…" rows={2} data-testid="input-template-description" />
          </div>

          <div>
            <div className="mb-3">
              <Button
                type="button"
                className="w-full bg-brand hover:bg-brand-hover text-brand-foreground"
                onClick={() => {
                  if (fields.length > 0) {
                    setLoadDefaultOpen(true);
                    return;
                  }
                  applyPadPulseDefault();
                }}
                data-testid="button-load-padpulse-default"
              >
                <Sparkles className="h-4 w-4 mr-2" /> Load PadPulse Default Template
              </Button>
            </div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base">Form fields</Label>
              <div className="flex items-center gap-2">
                <select
                  className="border rounded text-sm px-2 py-1"
                  value=""
                  onChange={(e) => { if (e.target.value) { setFields(resortByPage([...fields, { ...newField(e.target.value as BookingRequestFieldType), page: pageCount }])); e.target.value = ""; } }}
                  data-testid="select-add-field-type"
                >
                  <option value="">+ Add field…</option>
                  {BOOKING_REQUEST_FIELD_TYPES.map((t) => <option key={t} value={t}>{FIELD_TYPE_LABELS[t]}</option>)}
                </select>
              </div>
            </div>
            {fields.length === 0 ? (
              <div className="border rounded p-4 text-center space-y-2">
                <p className="text-sm text-gray-500">No fields yet.</p>
                <p className="text-xs text-gray-500">Start from scratch with the dropdown above, or click <span className="font-medium">PadPulse Default</span> to load a complete prospect-application template you can customize.</p>
              </div>
            ) : (
              <div className="space-y-5">
                {Array.from({ length: pageCount }, (_, i) => i + 1).map((pageNum) => {
                  const pageFields = fields.filter((f) => pageOf(f) === pageNum);
                  return (
                  <div key={`page-${pageNum}`} className="space-y-3 rounded-lg border border-dashed p-3" data-testid={`page-group-${pageNum}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700" data-testid={`page-header-${pageNum}`}>
                        Page {pageNum}{pageCount > 1 ? ` of ${pageCount}` : ""}
                      </span>
                      {pageCount > 1 && (
                        <button type="button" className="text-xs text-red-600 underline" onClick={() => deletePage(pageNum)} data-testid={`button-delete-page-${pageNum}`}>Remove page</button>
                      )}
                    </div>
                    {pageFields.length > 0 && unreachablePageNumbers.has(pageNum) && (
                      <div
                        className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-700"
                        data-testid={`warning-unreachable-page-${pageNum}`}
                      >
                        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <p>
                          Applicants will never see this page. Every question on it is hidden by a
                          show/hide rule that can never be satisfied — each one points to a question
                          that has been deleted, now appears later, or is itself never shown. Fix the
                          conditions below (or remove the broken ones) so at least one question can
                          appear.
                        </p>
                      </div>
                    )}
                    {pageFields.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No fields on this page yet — add a field, or move one here with the page selector.</p>
                    ) : pageFields.map((f) => {
                      const idx = fields.indexOf(f);
                      return (
                  <Card
                    key={f.id}
                    className={`p-3 space-y-2 transition-all ${dragOver === idx && dragIdx !== idx ? "border-blue-500 ring-2 ring-blue-200" : ""} ${dragIdx === idx ? "opacity-50" : ""}`}
                    draggable
                    onDragStart={onDragStart(idx)}
                    onDragOver={onDragOver(idx)}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop(idx)}
                    data-testid={`card-field-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs">
                        <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" data-testid={`handle-drag-${idx}`} />
                        <Badge variant="secondary">{FIELD_TYPE_LABELS[f.type] || f.type}</Badge>
                      </div>
                      <div className="flex gap-1 items-center">
                        {pageCount > 1 && (
                          <select
                            className="border rounded text-xs px-1 py-0.5 mr-1"
                            value={pageOf(f)}
                            onChange={(e) => moveFieldToPage(idx, Number(e.target.value))}
                            data-testid={`select-field-page-${idx}`}
                            title="Move to page"
                          >
                            {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
                              <option key={p} value={p}>Page {p}</option>
                            ))}
                          </select>
                        )}
                        {idx > 0 && (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            title={conditionsExpanded.has(f.id) ? "Hide conditions" : "Add/edit conditions"}
                            onClick={() => toggleConditions(f.id)}
                            data-testid={`button-toggle-conditions-${idx}`}
                          >
                            <GitBranch className={`h-4 w-4 ${(f.conditions?.length ?? 0) > 0 ? "text-blue-600" : "text-gray-400"}`} />
                          </Button>
                        )}
                        <Button type="button" size="icon" variant="ghost" onClick={() => setFields(fields.filter((_, i) => i !== idx))} data-testid={`button-remove-field-${idx}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    </div>
                    {staleConditionSourcesById.has(f.id) && (() => {
                      const staleSourceIds = staleConditionSourcesById.get(f.id)!;
                      // A stale source is "recoverable" when the field still
                      // exists in the template (it was reordered to now sit
                      // after this question) — we can name it. When it isn't
                      // found at all it was deleted; fall back to its raw id so
                      // the host still has something to search for.
                      const sources = staleSourceIds.map((sid) => {
                        const src = fields.find((x) => x.id === sid);
                        return { sid, label: src ? (singleLineLabel(src.label) || "Untitled question") : null };
                      });
                      const reordered = sources.filter((s) => s.label !== null);
                      const deleted = sources.filter((s) => s.label === null);
                      const multiple = staleSourceIds.length > 1;
                      return (
                        <div
                          className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-700"
                          data-testid={`warning-stale-condition-${idx}`}
                        >
                          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <div className="space-y-1.5">
                            <p>
                              This question's show/hide rule will never show it because it points to{" "}
                              {reordered.length > 0 && (
                                <span data-testid={`text-stale-reordered-${idx}`}>
                                  {reordered.map((s, i) => (
                                    <span key={s.sid}>
                                      {i > 0 ? (i === reordered.length - 1 ? " and " : ", ") : ""}
                                      <span className="font-medium">“{s.label}”</span>
                                    </span>
                                  ))}
                                  {reordered.length === 1 ? ", which now appears after it" : ", which now appear after it"}
                                  {deleted.length > 0 ? "; and " : "."}
                                </span>
                              )}
                              {deleted.length > 0 && (
                                <span data-testid={`text-stale-deleted-${idx}`}>
                                  {deleted.length === 1 ? "a question that has been deleted" : `${deleted.length} questions that have been deleted`}{" "}
                                  (<span className="font-mono">{deleted.map((s) => s.sid).join(", ")}</span>).
                                </span>
                              )}
                            </p>
                            <div className="flex flex-wrap gap-x-3 gap-y-1">
                              <button
                                type="button"
                                className="font-medium underline hover:opacity-70"
                                onClick={() => removeStaleConditionsFromField(idx)}
                                data-testid={`button-remove-stale-condition-${idx}`}
                              >
                                {multiple ? "Remove the broken conditions" : "Remove the broken condition"}
                              </button>
                              {idx > 0 && (
                                <button
                                  type="button"
                                  className="font-medium underline hover:opacity-70"
                                  onClick={() => { if (!conditionsExpanded.has(f.id)) toggleConditions(f.id); }}
                                  data-testid={`button-fix-stale-condition-${idx}`}
                                >
                                  Edit conditions
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="col-span-2">
                        <Label className="text-xs">Label</Label>
                        <TextToolbar
                          onBold={() => wrapFieldText(idx, "**")}
                          onItalic={() => wrapFieldText(idx, "_")}
                          onUnderline={() => wrapFieldText(idx, "__")}
                          onBullet={() => insertFieldListPrefix(idx, "- ")}
                          onNumbered={() => insertFieldListPrefix(idx, "1. ")}
                          testId={`toolbar-field-label-${idx}`}
                        />
                        <Textarea
                          id={`textarea-field-label-${f.id}`}
                          value={f.label}
                          onChange={(e) => setFields(fields.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))}
                          rows={Math.min(6, Math.max(1, (f.label || "").split("\n").length))}
                          className="min-h-0 resize-y"
                          data-testid={`input-field-label-${idx}`}
                        />
                        <FormattingHelp testId={`formatting-help-field-label-${idx}`} />
                      </div>
                      {!conditionsExpanded.has(f.id) && (f.conditions?.length ?? 0) > 0 && (() => {
                        const conds = f.conditions!;
                        const eligibleSet = new Set(fields.slice(0, idx).map((s) => s.id));
                        const staleCount = conds.filter((c) => !eligibleSet.has(c.sourceFieldId)).length;
                        const combinator = f.conditionCombinator ?? "all";
                        if (conds.length === 1) {
                          const cond = conds[0];
                          const isStale = !eligibleSet.has(cond.sourceFieldId);
                          if (isStale) {
                            return (
                              <Tooltip key="cond-summary">
                                <TooltipTrigger asChild>
                                  <div className="col-span-2 flex items-center gap-1.5 text-xs text-amber-600 cursor-pointer hover:opacity-70" onClick={() => toggleConditions(f.id)} data-testid={`condition-summary-${idx}`}>
                                    <GitBranch className="h-3 w-3 flex-shrink-0" />
                                    <span>⚠ Condition references a removed question</span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>Click to edit conditions</TooltipContent>
                              </Tooltip>
                            );
                          }
                          const srcField = fields.find((s) => s.id === cond.sourceFieldId);
                          const opLabel = cond.operator === "equals" ? "is" : cond.operator === "not-equals" ? "is not" : "is any of";
                          const valLabel = cond.values.map((v) => v === true ? "Yes" : v === false ? "No" : String(v)).join(", ");
                          return (
                            <Tooltip key="cond-summary">
                              <TooltipTrigger asChild>
                                <div className="col-span-2 flex items-center gap-1.5 text-xs text-blue-700 cursor-pointer hover:opacity-70" onClick={() => toggleConditions(f.id)} data-testid={`condition-summary-${idx}`}>
                                  <GitBranch className="h-3 w-3 flex-shrink-0 text-blue-600" />
                                  <span>Shown if <span className="font-medium">{srcField ? singleLineLabel(srcField.label) : "…"}</span> {opLabel} <span className="font-medium">{valLabel}</span></span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>Click to edit conditions</TooltipContent>
                            </Tooltip>
                          );
                        }
                        if (staleCount > 0) {
                          return (
                            <Tooltip key="cond-summary">
                              <TooltipTrigger asChild>
                                <div className="col-span-2 flex items-center gap-1.5 text-xs text-amber-600 cursor-pointer hover:opacity-70" onClick={() => toggleConditions(f.id)} data-testid={`condition-summary-${idx}`}>
                                  <GitBranch className="h-3 w-3 flex-shrink-0" />
                                  <span>⚠ {conds.length} conditions — {staleCount} reference{staleCount !== 1 ? "s" : ""} a removed question</span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>Click to edit conditions</TooltipContent>
                            </Tooltip>
                          );
                        }
                        return (
                          <Tooltip key="cond-summary">
                            <TooltipTrigger asChild>
                              <div className="col-span-2 flex items-center gap-1.5 text-xs text-blue-700 cursor-pointer hover:opacity-70" onClick={() => toggleConditions(f.id)} data-testid={`condition-summary-${idx}`}>
                                <GitBranch className="h-3 w-3 flex-shrink-0 text-blue-600" />
                                <span>Shown if <span className="font-medium">{combinator === "all" ? "all" : "any"}</span> of {conds.length} conditions met</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>Click to edit conditions</TooltipContent>
                          </Tooltip>
                        );
                      })()}
                      <div className="col-span-2">
                        <Label className="text-xs">Help text</Label>
                        <Input value={f.helpText || ""} onChange={(e) => setFields(fields.map((x, i) => i === idx ? { ...x, helpText: e.target.value } : x))} />
                      </div>
                      {(f.type === "select" || f.type === "multiselect" || f.type === "radio") && (
                        <div className="col-span-2 space-y-2">
                          <div>
                            <Label className="text-xs">Options (one per line)</Label>
                            <Textarea
                              value={optionDrafts[f.id] ?? (f.options || []).join("\n")}
                              onChange={(e) => setOptionDrafts((prev) => ({ ...prev, [f.id]: e.target.value }))}
                              onBlur={() => commitOptionDraft(f.id)}
                              rows={3}
                              data-testid={`textarea-options-${idx}`}
                            />
                          </div>
                          <label className="flex items-center gap-2 text-sm">
                            <Checkbox
                              checked={f.allowOther === true}
                              onCheckedChange={(v) => setFields(fields.map((x, i) => i === idx ? { ...x, allowOther: v === true } : x))}
                              data-testid={`checkbox-allow-other-${idx}`}
                            />
                            Include an "Other (please specify)" option
                          </label>
                        </div>
                      )}
                      {f.type === "agreement" && (
                        <div className="col-span-2">
                          <Label className="text-xs">Agreement text shown to user</Label>
                          <TextToolbar
                            onBold={() => wrapAgreementText(idx, "**")}
                            onItalic={() => wrapAgreementText(idx, "_")}
                            onUnderline={() => wrapAgreementText(idx, "__")}
                            onBullet={() => insertAgreementListPrefix(idx, "- ")}
                            onNumbered={() => insertAgreementListPrefix(idx, "1. ")}
                            testId={`toolbar-agreement-${idx}`}
                          />
                          <Textarea id={`textarea-agreement-${f.id}`} value={f.agreementText || ""} onChange={(e) => setFields(fields.map((x, i) => i === idx ? { ...x, agreementText: e.target.value } : x))} rows={4} />
                          <FormattingHelp testId={`formatting-help-agreement-${idx}`} />
                        </div>
                      )}
                      {(f.type === "photo" || f.type === "video_record" || f.type === "video_upload" || f.type === "file") && (
                        <div>
                          <Label className="text-xs">Max files</Label>
                          <Input type="number" min={1} max={10} value={f.maxFiles ?? 1} onChange={(e) => setFields(fields.map((x, i) => i === idx ? { ...x, maxFiles: parseInt(e.target.value) || 1 } : x))} />
                        </div>
                      )}
                      {(f.type === "video_record" || f.type === "video_upload") && (
                        <div>
                          <Label className="text-xs">Max duration (s)</Label>
                          <Input type="number" min={5} max={600} value={f.maxDurationSeconds ?? 60} onChange={(e) => setFields(fields.map((x, i) => i === idx ? { ...x, maxDurationSeconds: parseInt(e.target.value) || 60 } : x))} />
                        </div>
                      )}
                      <label className="flex items-center gap-2 text-sm pt-5 col-span-2">
                        <Checkbox checked={f.required} onCheckedChange={(v) => setFields(fields.map((x, i) => i === idx ? { ...x, required: v === true } : x))} data-testid={`checkbox-required-${idx}`} />
                        Required field
                      </label>
                      {idx > 0 && conditionsExpanded.has(f.id) && (
                        <ConditionsEditor
                          field={f}
                          fieldIndex={idx}
                          allFields={fields}
                          onChange={(updates) => setFields(fields.map((x, i) => i === idx ? { ...x, ...updates } : x))}
                        />
                      )}
                    </div>
                  </Card>
                      );
                    })}
                  </div>
                  );
                })}
                <Button type="button" variant="outline" className="w-full" onClick={addPage} data-testid="button-add-page">
                  <Plus className="h-4 w-4 mr-2" /> Add page
                </Button>
              </div>
            )}
          </div>

          <div className="border-t pt-4 space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Checkbox checked={overrideEnabled} onCheckedChange={(v) => setOverrideEnabled(v === true)} data-testid="checkbox-branding-override" />
              Override branding for this template
            </label>
            {overrideEnabled && (
              <div className="pl-6 space-y-3">
                <p className="text-xs text-gray-500">
                  Fields left blank inherit from your{" "}
                  <a
                    href="/app/members/booking-requests/settings"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline inline-flex items-center gap-0.5"
                    data-testid="link-booking-branding-settings"
                  >
                    Booking Request Branding settings
                    <ExternalLink className="h-3 w-3" />
                  </a>
                  .
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{BRANDING_FIELD_LABELS.brandName}</Label>
                    <Input
                      value={brandingOverride.brandName || ""}
                      onChange={(e) => setBrandingOverride({ ...brandingOverride, brandName: e.target.value })}
                      placeholder={inheritedDefaults.brandName || "(none)"}
                      data-testid="input-override-brandName"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">{BRANDING_FIELD_LABELS.accentColor}</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={brandingOverride.accentColor || inheritedDefaults.accentColor || DEFAULT_ACCENT_COLOR}
                        onChange={(e) => setBrandingOverride({ ...brandingOverride, accentColor: e.target.value })}
                        className="w-14 p-1 h-9"
                        data-testid="input-override-accentColor-swatch"
                      />
                      <Input
                        value={brandingOverride.accentColor || ""}
                        onChange={(e) => setBrandingOverride({ ...brandingOverride, accentColor: e.target.value })}
                        placeholder={inheritedDefaults.accentColor || DEFAULT_ACCENT_COLOR}
                        data-testid="input-override-accentColor"
                      />
                    </div>
                  </div>

                  <div className="col-span-2">
                    <Label className="text-xs">{BRANDING_FIELD_LABELS.brandLogoUrl}</Label>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      onChange={handleLogoUpload}
                      data-testid="input-override-brandLogoUrl-file"
                    />
                    {brandingOverride.brandLogoUrl ? (
                      <div className="flex items-center gap-3 mt-1 border rounded p-2 bg-gray-50">
                        <img src={appImagePreviewUrl(brandingOverride.brandLogoUrl)} alt="Override logo" className="h-10 w-10 object-contain rounded bg-white border" data-testid="img-override-logo-preview" />
                        <div className="flex-1 text-xs text-gray-600 truncate">Override logo set</div>
                        <Button type="button" variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} data-testid="button-override-replace-logo">
                          <Upload className="h-3.5 w-3.5 mr-1" /> Replace
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setBrandingOverride({ ...brandingOverride, brandLogoUrl: "" })} data-testid="button-override-remove-logo">
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 mt-1">
                        {inheritedDefaults.brandLogoUrl ? (
                          <div className="flex items-center gap-2 border rounded p-2 bg-gray-50 flex-1">
                            <img src={appImagePreviewUrl(inheritedDefaults.brandLogoUrl)} alt="Inherited logo" className="h-8 w-8 object-contain rounded bg-white border" data-testid="img-inherited-logo-preview" />
                            <span className="text-xs text-gray-500">Inheriting from settings</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 border rounded p-2 bg-gray-50 flex-1 text-xs text-gray-400" data-testid="text-inherited-logo-none">
                            No default logo set
                          </div>
                        )}
                        <Button type="button" variant="outline" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} className="mt-0 shrink-0" data-testid="button-override-upload-logo">
                          <Upload className="h-4 w-4 mr-2" /> {uploadingLogo ? "Uploading…" : inheritedDefaults.brandLogoUrl ? "Override logo" : "Upload logo"}
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG, WebP, or GIF up to 5MB. Leave blank to use the default logo.</p>
                  </div>

                  <div className="col-span-2">
                    <Label className="text-xs">{BRANDING_FIELD_LABELS.headline}</Label>
                    <Input
                      value={brandingOverride.headline || ""}
                      onChange={(e) => setBrandingOverride({ ...brandingOverride, headline: e.target.value })}
                      placeholder={inheritedDefaults.headline || "(none)"}
                      data-testid="input-override-headline"
                    />
                  </div>

                  <div className="col-span-2">
                    <Label className="text-xs">{BRANDING_FIELD_LABELS.description}</Label>
                    <TextToolbar
                      onBold={() => wrapOverrideDescription("**")}
                      onItalic={() => wrapOverrideDescription("_")}
                      onUnderline={() => wrapOverrideDescription("__")}
                      onBullet={insertOverrideBullets}
                      onNumbered={insertOverrideNumbered}
                      testId="toolbar-override-description"
                    />
                    <Textarea
                      ref={overrideDescRef}
                      value={brandingOverride.description || ""}
                      onChange={(e) => setBrandingOverride({ ...brandingOverride, description: e.target.value })}
                      placeholder={inheritedDefaults.description || "(none)"}
                      rows={4}
                      data-testid="input-override-description"
                    />
                    <FormattingHelp testId="formatting-help-override-description" />
                  </div>

                  <div>
                    <Label className="text-xs">{BRANDING_FIELD_LABELS.contactPhone}</Label>
                    <Input
                      value={brandingOverride.contactPhone || ""}
                      onChange={(e) => setBrandingOverride({ ...brandingOverride, contactPhone: e.target.value })}
                      placeholder={inheritedDefaults.contactPhone || "(none)"}
                      data-testid="input-override-contactPhone"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">{BRANDING_FIELD_LABELS.contactEmail}</Label>
                    <Input
                      value={brandingOverride.contactEmail || ""}
                      onChange={(e) => setBrandingOverride({ ...brandingOverride, contactEmail: e.target.value })}
                      placeholder={inheritedDefaults.contactEmail || "(none)"}
                      data-testid="input-override-contactEmail"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">{BRANDING_FIELD_LABELS.ctaButtonText}</Label>
                    <Input
                      value={brandingOverride.ctaButtonText || ""}
                      onChange={(e) => setBrandingOverride({ ...brandingOverride, ctaButtonText: e.target.value })}
                      placeholder={inheritedDefaults.ctaButtonText || "Submit Request"}
                      data-testid="input-override-ctaButtonText"
                    />
                  </div>

                  <div>
                    <Label className="text-xs">{BRANDING_FIELD_LABELS.footerText}</Label>
                    <Input
                      value={brandingOverride.footerText || ""}
                      onChange={(e) => setBrandingOverride({ ...brandingOverride, footerText: e.target.value })}
                      placeholder={inheritedDefaults.footerText || "(none)"}
                      data-testid="input-override-footerText"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={isActive} onCheckedChange={(v) => setIsActive(v === true)} data-testid="checkbox-template-active" />
            Active (accepting submissions)
          </label>
        </div>

        {showPreview && (
          <div className="border-l pl-6 space-y-3" data-testid="panel-template-preview" data-tour="booking-requests-form">
            <div className="text-xs uppercase tracking-wide text-gray-500">Live preview</div>
            <div className="rounded border bg-white p-4 max-w-sm mx-auto shadow-sm">
              <div className="mb-3">
                <div className="font-bold text-lg" data-testid="text-preview-name">{name || "Untitled form"}</div>
                {description && <div className="text-sm text-gray-500">{description}</div>}
              </div>
              {fields.length === 0 ? (
                <div className="text-sm text-gray-400 text-center py-6">Add fields to see them here.</div>
              ) : (
                <div className="space-y-3">
                  {fields.map((f) => (
                    <div key={f.id} className="space-y-1">
                      <div className="text-sm font-medium whitespace-pre-line">{renderMarkdown(f.label)}{f.required && <span className="text-red-500"> *</span>}</div>
                      {f.helpText && <div className="text-xs text-gray-500">{f.helpText}</div>}
                      {(f.type === "text" || f.type === "email" || f.type === "phone" || f.type === "number" || f.type === "date") && (
                        <Input disabled placeholder={f.placeholder || ""} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} />
                      )}
                      {f.type === "longtext" && <Textarea disabled rows={3} placeholder={f.placeholder || ""} />}
                      {f.type === "select" && (
                        <select disabled className="w-full border rounded h-10 px-2 text-sm bg-gray-50">
                          <option>Choose…</option>
                          {(f.options || []).map((o, i) => <option key={i}>{o}</option>)}
                        </select>
                      )}
                      {f.type === "multiselect" && (
                        <div className="space-y-1">
                          {(f.options || []).map((o, i) => (
                            <label key={i} className="flex items-center gap-2 text-sm"><Checkbox disabled />{o}</label>
                          ))}
                        </div>
                      )}
                      {f.type === "yesno" && (
                        <div className="flex gap-2"><Button type="button" variant="outline" disabled className="flex-1">Yes</Button><Button type="button" variant="outline" disabled className="flex-1">No</Button></div>
                      )}
                      {f.type === "radio" && (
                        <div className="space-y-1">
                          {(f.options || []).map((o, i) => (
                            <label key={i} className="flex items-center gap-2 text-sm"><input type="radio" disabled className="h-4 w-4" />{o}</label>
                          ))}
                        </div>
                      )}
                      {f.type === "photo" && <Button type="button" variant="outline" disabled className="w-full">📷 Take photo / Choose (0/{f.maxFiles ?? 6})</Button>}
                      {f.type === "video_record" && <Button type="button" variant="outline" disabled className="w-full">🎥 Record video (≤{f.maxDurationSeconds ?? 60}s)</Button>}
                      {f.type === "video_upload" && <Button type="button" variant="outline" disabled className="w-full">📤 Upload video (≤{f.maxDurationSeconds ?? 60}s)</Button>}
                      {f.type === "signature" && <div className="border rounded h-24 bg-gray-50 flex items-center justify-center text-xs text-gray-400">Signature pad</div>}
                      {f.type === "agreement" && (
                        <label className="flex gap-2 items-start text-sm border rounded p-2 bg-gray-50">
                          <Checkbox disabled className="mt-0.5" />
                          <span className="space-y-1">{f.agreementText ? renderMarkdown(f.agreementText) : "I agree."}</span>
                        </label>
                      )}
                      {f.type === "file" && <Button type="button" variant="outline" disabled className="w-full">📎 Choose files (0/{f.maxFiles ?? 3})</Button>}
                    </div>
                  ))}
                  <Button disabled className="w-full mt-2">Submit</Button>
                </div>
              )}
            </div>
          </div>
        )}
        </div>

        {uploadingLogo && (
          <p className="text-sm text-gray-500 flex items-center gap-1.5" data-testid="text-template-uploading-hint">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Logo is still uploading — please wait before saving or closing.
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploadingLogo} data-testid="button-cancel-template">Cancel</Button>
          <Button onClick={attemptSave} disabled={save.isPending || !name.trim() || uploadingLogo} data-testid="button-save-template">
            {save.isPending ? "Saving…" : uploadingLogo ? "Uploading logo…" : "Save template"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <AlertDialog open={loadDefaultOpen} onOpenChange={setLoadDefaultOpen}>
        <AlertDialogContent data-testid="dialog-load-padpulse-default-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Replace current fields?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace the current fields with the PadPulse Default template.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-load-padpulse-default">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-load-padpulse-default"
              onClick={(e) => {
                e.preventDefault();
                applyPadPulseDefault();
                setLoadDefaultOpen(false);
              }}
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={staleWarning !== null} onOpenChange={(v) => { if (!v) setStaleWarning(null); }}>
        <AlertDialogContent data-testid="dialog-stale-conditions-warning">
          <AlertDialogHeader>
            <AlertDialogTitle>Some conditions reference a removed question</AlertDialogTitle>
            <AlertDialogDescription>
              These questions have show/hide conditions that point to a question that was deleted or moved after them, so the logic can never run:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="list-disc pl-5 text-sm text-amber-700 space-y-1" data-testid="list-stale-condition-fields">
            {(staleWarning ?? []).map((label, i) => (
              <li key={i} data-testid={`text-stale-field-${i}`}>{label}</li>
            ))}
          </ul>
          <p className="text-xs text-gray-500">
            You can remove the broken conditions and save, or go back to fix them first.
          </p>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-stale-conditions">Go back and fix</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-remove-stale-conditions"
              onClick={(e) => { e.preventDefault(); removeStaleAndSave(); }}
            >
              Remove conditions & save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={unreachablePageWarning !== null} onOpenChange={(v) => { if (!v) setUnreachablePageWarning(null); }}>
        <AlertDialogContent data-testid="dialog-unreachable-page-warning">
          <AlertDialogHeader>
            <AlertDialogTitle>This form has a page applicants can never see</AlertDialogTitle>
            <AlertDialogDescription>
              Every question on the following {(unreachablePageWarning?.length ?? 0) === 1 ? "page is" : "pages are"} hidden by a show/hide rule that can never be satisfied, so applicants would never reach {(unreachablePageWarning?.length ?? 0) === 1 ? "it" : "them"}:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="list-disc pl-5 text-sm text-amber-700 space-y-1" data-testid="list-unreachable-pages">
            {(unreachablePageWarning ?? []).map((pageNum) => (
              <li key={pageNum} data-testid={`text-unreachable-page-${pageNum}`}>Page {pageNum}</li>
            ))}
          </ul>
          <p className="text-xs text-gray-500">
            Fix or remove the broken conditions so at least one question on each page can appear, then save.
          </p>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-unreachable-page">Go back and fix</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

function PreviewDialog({ template, open, onOpenChange }: { template: BookingRequestTemplate | null; open: boolean; onOpenChange: (v: boolean) => void }) {
  const { toast } = useToast();
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !template) {
      setToken(null);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/app/booking-requests/templates/${template.id}/links`, { credentials: "include" });
        if (!res.ok) throw new Error("Failed to load links");
        const links = (await res.json()) as Array<{ token: string; isActive?: boolean }>;
        let active = links.find((l) => l.isActive !== false);
        if (!active) {
          const created = await apiRequest("POST", `/api/app/booking-requests/templates/${template.id}/links`, { propertyId: null });
          active = (await created.json()) as { token: string };
        }
        if (!cancelled) setToken(active.token);
      } catch (e: any) {
        if (!cancelled) setError(parseErrorMessage(e, "Couldn't load the preview. Please try again."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, template]);

  const url = token ? `${window.location.origin}/booking-request/${token}` : "";

  const copyLink = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    toast({ title: "Link copied", description: url });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 max-h-[90vh] flex flex-col">
        <DialogHeader className="px-4 pt-4 pb-3 border-b">
          <DialogTitle>Preview — {template?.name}</DialogTitle>
        </DialogHeader>
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
          <Input
            readOnly
            value={loading ? "Loading…" : url}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 text-xs font-mono bg-white"
            data-testid="input-preview-url"
          />
          <Button size="sm" variant="outline" onClick={copyLink} disabled={!url} data-testid="button-preview-copy-link">
            <Copy className="h-4 w-4 mr-1" /> Copy Link
          </Button>
          <Button size="sm" variant="ghost" asChild disabled={!url}>
            <a href={url || undefined} target="_blank" rel="noreferrer" data-testid="button-preview-open-tab" aria-disabled={!url}>
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </div>
        <div className="flex-1 min-h-0 bg-gray-100">
          {error ? (
            <div className="flex items-center justify-center h-full p-6 text-center text-sm text-red-500" data-testid="text-preview-error">{error}</div>
          ) : !token ? (
            <div className="flex items-center justify-center h-full p-6 text-sm text-gray-500">Loading preview…</div>
          ) : (
            <iframe
              src={url}
              title="Booking request preview"
              className="w-full h-[70vh] border-0 bg-white"
              data-testid="iframe-preview"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Extensions for the synthesized `?fn=` download names on submission media
// (the stored object paths are extensionless UUIDs).
const MEDIA_CT_TO_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/heic": "heic",
  "image/heif": "heif",
  "application/pdf": "pdf",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/quicktime": "mov",
};

export function SubmissionDetail({ id, properties, onClose }: { id: number; properties: Property[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const detailQuery = useQuery<{
    submission: BookingRequestSubmission;
    template: BookingRequestTemplate;
    mediaMeta?: Record<string, Array<{ contentType: string | null }>>;
    reviewedByName?: string | null;
    // Where this applicant is headed, resolved server-side: the property from
    // the submission's unique link, and the room from the member the
    // submission resolved to. Either may be null (no link property, no
    // resolved member, or no room assigned yet).
    property?: { id: number; name: string | null } | null;
    room?: { id: number; roomNumber: string | null; propertyId: number | null } | null;
  }>({
    queryKey: ["/api/app/booking-requests/submissions", id],
    queryFn: async () => {
      const res = await fetch(`/api/app/booking-requests/submissions/${id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const [reviewNotes, setReviewNotes] = useState("");
  const [rejectionCategory, setRejectionCategory] = useState<string>("");
  const [photoIdx, setPhotoIdx] = useState<number | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Seed the review fields from the saved submission once it loads — the
  // stored rejection reason used to be shown only as placeholder text, which
  // read as background hint copy instead of the actual saved note.
  const seededReviewRef = useRef(false);
  useEffect(() => {
    const loaded = detailQuery.data?.submission;
    if (!seededReviewRef.current && loaded) {
      seededReviewRef.current = true;
      setReviewNotes(loaded.reviewNotes ?? "");
      setRejectionCategory(loaded.rejectionCategory ?? "");
    }
  }, [detailQuery.data]);

  // Attachments are served through the submission-scoped media route (the raw
  // /objects/ paths carry an owner-only ACL that 403s for teammates, and the
  // stored paths are extensionless UUIDs). The ?ct=/?fn= annotations let the
  // shared attachment helpers classify the file and pick a download name.
  const mediaMeta = detailQuery.data?.mediaMeta ?? {};
  const mediaContentType = (fieldId: string, idx: number): string | null =>
    mediaMeta[fieldId]?.[idx]?.contentType ?? null;
  const proxyMediaUrl = (fieldId: string, idx: number, label: string): string => {
    const ct = mediaContentType(fieldId, idx);
    const params = new URLSearchParams();
    if (ct) params.set("ct", ct);
    const ext = ct ? MEDIA_CT_TO_EXT[ct] : undefined;
    params.set("fn", `${label.replace(/[^\w -]+/g, "").trim() || "attachment"}-${idx + 1}${ext ? `.${ext}` : ""}`);
    return `/api/app/booking-requests/submissions/${id}/media/${fieldId}/${encodeURIComponent(String(idx))}?${params.toString()}`;
  };

  // Every image attachment across all fields, in render order, so the shared
  // PhotoLightbox can page through the whole submission.
  const imageItems = useMemo(() => {
    const data = detailQuery.data;
    if (!data?.submission || !data?.template) return [] as Array<{ fieldId: string; idx: number }>;
    const responses = (data.submission.responses ?? {}) as Record<string, unknown>;
    const mediaUrls = (data.submission.mediaUrls ?? {}) as Record<string, string[]>;
    const fields = (data.template.fields as BookingRequestField[]) || [];
    const visible = computeVisibleFieldIds(fields, responses);
    const items: Array<{ fieldId: string; idx: number }> = [];
    for (const f of fields) {
      if (!visible.has(f.id)) continue;
      const media = mediaUrls[f.id] || [];
      media.forEach((_, i) => {
        const ct = data.mediaMeta?.[f.id]?.[i]?.contentType ?? null;
        const isImage = f.type === "photo" || f.type === "signature" || (ct != null && ct.startsWith("image/"));
        if (isImage) {
          items.push({ fieldId: f.id, idx: i });
        }
      });
    }
    return items;
  }, [detailQuery.data]);
  const imageLightboxUrls = useMemo(() => {
    const data = detailQuery.data;
    if (!data?.template) return [] as string[];
    const fields = (data.template.fields as BookingRequestField[]) || [];
    const labelFor = new Map(fields.map((f) => [f.id, f.label] as const));
    return imageItems.map((it) => proxyMediaUrl(it.fieldId, it.idx, labelFor.get(it.fieldId) || "photo"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageItems, detailQuery.data]);
  const imageIndexOf = (fieldId: string, idx: number): number =>
    imageItems.findIndex((it) => it.fieldId === fieldId && it.idx === idx);

  const setStatus = useMutation({
    mutationFn: async (status: BookingRequestStatus) =>
      apiRequest("PATCH", `/api/app/booking-requests/submissions/${id}/status`, {
        status,
        reviewNotes,
        ...(status === "rejected" ? { rejectionCategory } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/booking-requests/submissions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/booking-requests/submissions", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/booking-requests/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/booking-requests/stats/ai-summary"] });
      toast({ title: "Status updated" });
      onClose();
    },
    onError: (err) => {
      toast({ title: parseErrorMessage(err, "Failed to update status"), variant: "destructive" });
    },
  });

  const sub = detailQuery.data?.submission;
  const tpl = detailQuery.data?.template;

  // Two-step inline confirmation. null = idle (shows hint note).
  const [pendingAction, setPendingAction] = useState<BookingRequestStatus | null>(null);
  // Messaging rules, fetched while a decision is possible so the confirm
  // panel can say whether an automatic approved/rejected message will fire.
  const rulesQuery = useQuery<MessagingRule[]>({
    queryKey: ["/api/app/messaging-rules"],
    queryFn: async () => {
      const res = await fetch("/api/app/messaging-rules", { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: sub?.status === "pending",
  });
  // Property groups — needed to resolve group-scoped rules.
  const propertyGroupsQuery = usePropertyGroups();
  const decisionPropertyId = detailQuery.data?.property?.id ?? sub?.propertyId ?? null;
  // The group name for the submission's property, resolved from the passed-in
  // properties list (the full Property objects include propertyGroup).
  const decisionPropertyGroupName = useMemo((): string | null => {
    if (decisionPropertyId == null) return null;
    const p = properties.find((x) => x.id === decisionPropertyId);
    return p?.propertyGroup ?? null;
  }, [decisionPropertyId, properties]);
  const decisionPropertyGroupId = useMemo((): number | null => {
    if (!decisionPropertyGroupName || !propertyGroupsQuery.data) return null;
    const g = propertyGroupsQuery.data.find((g) => g.name === decisionPropertyGroupName);
    return g?.id ?? null;
  }, [decisionPropertyGroupName, propertyGroupsQuery.data]);

  // Mirrors the server dispatcher's property + group scoping logic.
  const findAutoMessageRule = (trigger: "booking_approved" | "booking_rejected"): MessagingRule | null => {
    const rules = Array.isArray(rulesQuery.data) ? rulesQuery.data : [];
    return (
      rules.find((r) => {
        if (!r.enabled || r.ruleType !== "event" || r.eventTrigger !== trigger) return false;
        const propStr = String(r.properties ?? "").trim();
        const groupStr = String((r as any).propertyGroupIds ?? "").trim();
        const hasIdConstraint = propStr !== "";
        const hasGroupConstraint = groupStr !== "";
        // No constraints → fires for all properties.
        if (!hasIdConstraint && !hasGroupConstraint) return true;
        if (decisionPropertyId == null) return false;
        if (hasIdConstraint) {
          const ids = propStr.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n));
          if (ids.includes(decisionPropertyId)) return true;
        }
        if (hasGroupConstraint && decisionPropertyGroupId != null) {
          const gids = groupStr.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n));
          if (gids.includes(decisionPropertyGroupId)) return true;
        }
        return false;
      }) ?? null
    );
  };
  const confirmRule = pendingAction === "approved"
    ? findAutoMessageRule("booking_approved")
    : pendingAction === "rejected"
      ? findAutoMessageRule("booking_rejected")
      : null;

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Booking request #{id}</DialogTitle></DialogHeader>
        {!sub || !tpl ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge>{sub.status}</Badge>
              <span className="text-sm text-gray-500">{new Date(sub.submittedAt).toLocaleString()}</span>
            </div>
            {/* Property / room this request is tied to, deep-linked so a
                reviewer can jump straight to either. Falls back to the local
                properties list when the server couldn't resolve the property
                (e.g. historical rows). */}
            {(() => {
              const linkedProperty =
                detailQuery.data?.property ??
                (sub.propertyId != null
                  ? (() => {
                      const matched = properties.find((p) => p.id === sub.propertyId);
                      return matched ? { id: matched.id, name: matched.name || matched.address || null } : null;
                    })()
                  : null);
              const linkedRoom = detailQuery.data?.room ?? null;
              if (!linkedProperty && !linkedRoom) return null;
              const roomPropertyId = linkedRoom?.propertyId ?? linkedProperty?.id ?? null;
              return (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid="submission-location">
                  {linkedProperty && (
                    <span>
                      <span className="text-gray-500">Property:</span>{" "}
                      <Link
                        href={`/app/properties/${linkedProperty.id}`}
                        className="font-medium text-brand hover:underline"
                        data-testid="link-submission-property"
                      >
                        {linkedProperty.name || `Property #${linkedProperty.id}`}
                      </Link>
                    </span>
                  )}
                  {linkedRoom && (
                    <span>
                      <span className="text-gray-500">Room:</span>{" "}
                      {roomPropertyId != null ? (
                        <Link
                          href={`/app/properties/${roomPropertyId}?tab=rooms`}
                          className="font-medium text-brand hover:underline"
                          data-testid="link-submission-room"
                        >
                          {linkedRoom.roomNumber || `Room #${linkedRoom.id}`}
                        </Link>
                      ) : (
                        <span className="font-medium">{linkedRoom.roomNumber || `Room #${linkedRoom.id}`}</span>
                      )}
                    </span>
                  )}
                </div>
              );
            })()}
            {sub.status !== "pending" && sub.reviewedAt && (
              <div className="text-xs text-gray-600" data-testid="text-submission-decision">
                {sub.status === "approved" ? "Approved" : sub.status === "rejected" ? "Rejected" : "Archived"}
                {detailQuery.data?.reviewedByName ? ` by ${detailQuery.data.reviewedByName}` : ""}
                {" · "}{new Date(sub.reviewedAt).toLocaleString()}
                {/* The reason is the headline for a rejection, not buried in
                    the notes field below. */}
                {sub.status === "rejected" && (sub.rejectionCategory || sub.reviewNotes) ? (
                  <span className="text-gray-800 font-medium">
                    {" — "}
                    {sub.rejectionCategory ? `${bookingRejectionCategoryLabel(sub.rejectionCategory)}${sub.reviewNotes ? ": " : ""}` : ""}
                    {sub.reviewNotes ?? ""}
                  </span>
                ) : null}
              </div>
            )}
            <div className="text-xs text-gray-500 space-y-0.5">
              {sub.ipAddress && <div data-testid="text-submission-ip">IP: {sub.ipAddress}</div>}
              {sub.userAgent && <div className="truncate" data-testid="text-submission-ua">User agent: {sub.userAgent}</div>}
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Name:</span>{" "}
                {sub.memberId != null ? (
                  <Link
                    href={`/app/members/${sub.memberId}`}
                    className="font-medium text-brand hover:underline"
                    data-testid="link-submission-member"
                  >
                    {sub.submitterName || `Member #${sub.memberId}`}
                  </Link>
                ) : (
                  sub.submitterName || "—"
                )}
              </div>
              <div><span className="text-gray-500">Email:</span> {sub.submitterEmail || "—"}</div>
              <div><span className="text-gray-500">Phone:</span> {sub.submitterPhone || "—"}</div>
            </div>

            <div className="space-y-3 border rounded p-3 bg-gray-50" data-tour="booking-request-settings-form">
              {(() => {
                const allFields = (tpl.fields as BookingRequestField[]) || [];
                const responses = (sub.responses ?? {}) as Record<string, unknown>;
                // Conditionally-hidden questions were never asked — showing
                // them (as "—") just clutters review, so mirror the live
                // form's visibility engine over the stored answers.
                const visibleIds = computeVisibleFieldIds(allFields, responses);
                return allFields.filter((f) => visibleIds.has(f.id));
              })().map((f) => {
                const responses = (sub.responses ?? {}) as Record<string, unknown>;
                const mediaUrls = (sub.mediaUrls ?? {}) as Record<string, string[]>;
                const v = responses[f.id];
                const media: string[] = mediaUrls[f.id] || [];
                const isImageAt = (i: number): boolean => {
                  const ct = mediaContentType(f.id, i);
                  return f.type === "photo" || f.type === "signature" || (ct != null && ct.startsWith("image/"));
                };
                const renderImageThumb = (i: number) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPhotoIdx(imageIndexOf(f.id, i))}
                    title="Click to view full size"
                    className="block cursor-zoom-in rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                    data-testid={`link-submission-photo-${f.id}-${i}`}
                  >
                    <img src={proxyMediaUrl(f.id, i, f.label)} alt={f.label} loading="lazy" className="rounded border max-h-32 w-full object-cover transition hover:opacity-90 hover:ring-2 hover:ring-brand" />
                  </button>
                );
                return (
                  <div key={f.id}>
                    <div className="text-xs font-medium text-gray-600 whitespace-pre-line">{f.label}</div>
                    {(f.type === "photo" || f.type === "signature") && (
                      <div className="grid grid-cols-3 gap-2 mt-1">
                        {media.map((_, i) => renderImageThumb(i))}
                      </div>
                    )}
                    {(f.type === "video_record" || f.type === "video_upload") && media.map((_, i) => <video key={i} src={proxyMediaUrl(f.id, i, f.label)} controls className="w-full rounded mt-1" />)}
                    {f.type === "file" && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {media.map((_, i) => {
                          if (isImageAt(i)) return renderImageThumb(i);
                          const ct = mediaContentType(f.id, i);
                          const url = proxyMediaUrl(f.id, i, f.label);
                          const name = downloadFileNameFor(url);
                          if (ct === "application/pdf") {
                            return (
                              <button
                                key={i}
                                type="button"
                                onClick={() => setPdfUrl(url)}
                                className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-brand hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                                title="Click to view"
                                data-testid={`link-submission-file-${f.id}-${i}`}
                              >
                                <FileText className="h-4 w-4 shrink-0" />
                                <span className="max-w-[220px] truncate">{name}</span>
                              </button>
                            );
                          }
                          return (
                            <a
                              key={i}
                              href={url}
                              download={name}
                              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-brand hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                              title="Download"
                              data-testid={`link-submission-file-${f.id}-${i}`}
                            >
                              <FileIcon className="h-4 w-4 shrink-0" />
                              <span className="max-w-[220px] truncate">{name}</span>
                              <Download className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            </a>
                          );
                        })}
                      </div>
                    )}
                    {f.type === "agreement" && <div className="text-sm">{v === true ? "✓ Accepted" : "Not accepted"}</div>}
                    {!["photo", "video_record", "video_upload", "signature", "file", "agreement"].includes(f.type) && (() => {
                      const isChoice = f.type === "radio" || f.type === "select" || f.type === "multiselect";
                      const opts = isChoice && f.allowOther ? (f.options || []) : null;
                      const isOther = (val: unknown): boolean =>
                        opts != null && typeof val === "string" && val.trim() !== "" && !opts.includes(val);
                      const renderValue = (val: unknown, key?: string | number) => {
                        const other = isOther(val);
                        return (
                          <span key={key} className="inline-flex items-center gap-1">
                            {other && (
                              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 font-normal" data-testid={`badge-other-${f.id}${key != null ? `-${key}` : ""}`}>
                                Other
                              </Badge>
                            )}
                            <span className={other ? "italic" : ""}>{String(val)}</span>
                          </span>
                        );
                      };
                      if (f.type === "yesno") {
                        return <div className="text-sm whitespace-pre-wrap">{v === true ? "Yes" : v === false ? "No" : "—"}</div>;
                      }
                      if (Array.isArray(v)) {
                        if (v.length === 0) return <div className="text-sm whitespace-pre-wrap">—</div>;
                        return (
                          <div className="text-sm whitespace-pre-wrap flex flex-wrap gap-x-1 gap-y-1 items-center">
                            {(v as unknown[]).map((item, i) => (
                              <span key={i} className="inline-flex items-center">
                                {renderValue(item, i)}
                                {i < v.length - 1 && <span>,</span>}
                              </span>
                            ))}
                          </div>
                        );
                      }
                      if (v == null || v === "") return <div className="text-sm whitespace-pre-wrap">—</div>;
                      return <div className="text-sm whitespace-pre-wrap">{renderValue(v)}</div>;
                    })()}
                  </div>
                );
              })}
            </div>

            <div className="space-y-2">
              {sub.status === "pending" && (
                <div>
                  <Label>Rejection category</Label>
                  <Select value={rejectionCategory || undefined} onValueChange={setRejectionCategory}>
                    <SelectTrigger className="w-full" data-testid="select-rejection-category">
                      <SelectValue placeholder="Required to reject · pick the closest reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {BOOKING_REJECTION_CATEGORIES.map((c) => (
                        <SelectItem key={c.key} value={c.key} data-testid={`option-rejection-category-${c.key}`}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Review notes (internal)</Label>
                <Textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} rows={2} placeholder="Required for reject / archive · optional for approve" data-testid="input-review-notes" />
              </div>
            </div>

            {sub.status === "pending" && (
              <div className="space-y-3">
                {/* Hint note — visible only when no action is staged yet */}
                {pendingAction == null && (
                  <div className="flex items-center gap-2 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700" data-testid="text-action-hint">
                    <Info className="h-3.5 w-3.5 shrink-0" />
                    Click a button to see more details, then click again to confirm.
                  </div>
                )}

                {/* Inline confirmation panel */}
                {pendingAction != null && (
                  <div className="rounded border border-gray-200 bg-gray-50 p-3 space-y-2 text-sm" data-testid="panel-confirm-action">
                    <p className="font-medium text-gray-800">
                      {pendingAction === "approved" ? "Approve this booking request?" : pendingAction === "rejected" ? "Reject this booking request?" : "Archive without a decision?"}
                    </p>
                    {pendingAction === "archived" ? (
                      <p className="text-gray-600 text-xs" data-testid="text-decision-effect">
                        Archiving closes this request locally without recording a decision. Nothing is sent to the applicant, and nothing is synced to PadSplit. Use this when the applicant cancelled or the request is stale.
                      </p>
                    ) : (
                      <p className="text-gray-600 text-xs" data-testid="text-decision-effect">
                        This records your {pendingAction === "approved" ? "approval" : "rejection"} in PadPulse and automatically syncs the decision to PadSplit — no extra steps needed on the PadSplit side.
                      </p>
                    )}
                    {(pendingAction === "approved" || pendingAction === "rejected") && (
                      <p className="text-xs text-gray-500" data-testid="text-decision-auto-message">
                        {rulesQuery.isLoading || propertyGroupsQuery.isLoading ? (
                          "Checking your message rules…"
                        ) : confirmRule ? (
                          <>
                            An automatic message will{" "}
                            {confirmRule.messageApproval === "review_before_sending"
                              ? "be drafted for your review"
                              : "be sent to the member"}{" "}
                            by the rule{" "}
                            <Link
                              href={`/app/messages/rules/${confirmRule.id}`}
                              className="font-medium text-brand underline"
                              data-testid="link-decision-rule"
                            >
                              {confirmRule.name}
                            </Link>
                            .
                          </>
                        ) : (
                          <>
                            No automatic {pendingAction === "approved" ? "approval" : "rejection"} message rule is set up
                            {decisionPropertyId == null ? " (this submission has no property)" : " for this property"}
                            {" — the member will not be messaged. "}
                            <Link
                              href="/app/messages?tab=rules"
                              className="text-brand underline"
                              data-testid="link-decision-setup-rule"
                            >
                              Set up a rule
                            </Link>
                          </>
                        )}
                      </p>
                    )}
                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPendingAction(null)}
                        data-testid="button-decision-cancel"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        disabled={setStatus.isPending}
                        variant={pendingAction === "rejected" ? "destructive" : pendingAction === "archived" ? "outline" : "default"}
                        onClick={() => { setStatus.mutate(pendingAction!); setPendingAction(null); }}
                        data-testid="button-decision-confirm"
                      >
                        {setStatus.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                        {pendingAction === "approved" ? "Confirm Approve" : pendingAction === "rejected" ? "Confirm Reject" : "Confirm Archive"}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 justify-end">
                  {/* Archive: the applicant cancelled before a decision. */}
                  <Button
                    variant={pendingAction === "archived" ? "secondary" : "outline"}
                    disabled={setStatus.isPending}
                    onClick={() => {
                      if (pendingAction === "archived") { setPendingAction(null); return; }
                      if (!reviewNotes.trim()) {
                        toast({ title: "A review note is required when archiving", variant: "destructive" });
                        return;
                      }
                      setPendingAction("archived");
                    }}
                    title="Applicant cancelled — close without a decision"
                    data-testid="button-archive-submission"
                  >
                    <Archive className="h-4 w-4 mr-1" /> Archive
                  </Button>
                  <Button
                    variant={pendingAction === "rejected" ? "secondary" : "destructive"}
                    disabled={setStatus.isPending}
                    onClick={() => {
                      if (pendingAction === "rejected") { setPendingAction(null); return; }
                      if (!rejectionCategory) {
                        toast({ title: "Pick a rejection category first", variant: "destructive" });
                        return;
                      }
                      if (!reviewNotes.trim()) {
                        toast({ title: "A review note is required when rejecting", variant: "destructive" });
                        return;
                      }
                      setPendingAction("rejected");
                    }}
                    data-testid="button-reject-submission"
                  >
                    <X className="h-4 w-4 mr-1" /> Reject
                  </Button>
                  <Button
                    variant={pendingAction === "approved" ? "secondary" : "default"}
                    disabled={setStatus.isPending}
                    onClick={() => {
                      if (pendingAction === "approved") { setPendingAction(null); return; }
                      setPendingAction("approved");
                    }}
                    data-testid="button-approve-submission"
                  >
                    <Check className="h-4 w-4 mr-1" /> Approve
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
      <PhotoLightbox
        images={imageLightboxUrls}
        index={photoIdx}
        messageId={`submission-${id}`}
        onIndexChange={setPhotoIdx}
        onClose={() => setPhotoIdx(null)}
      />
      <PdfLightbox url={pdfUrl} idPrefix={`submission-${id}`} onClose={() => setPdfUrl(null)} />
    </Dialog>
  );
}

export default function AppMembersBookingRequests() {
  const queryClient = useQueryClient();
  usePageTitle("Booking Requests");
  const [location] = useLocation();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<BookingRequestTemplate | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<BookingRequestTemplate | null>(null);
  const [openSubmission, setOpenSubmission] = useState<number | null>(null);
  const [tab, setTab] = useTabQueryParam("tab", "submissions", {
    tabs: ["submissions", "templates", "stats"],
  });

  // Open from query param
  useEffect(() => {
    const url = new URL(window.location.href);
    const sid = url.searchParams.get("submission");
    if (sid) setOpenSubmission(parseInt(sid));
  }, [location]);

  const templatesQuery = useQuery<(BookingRequestTemplate & { createdByName?: string | null })[]>({ queryKey: ["/api/app/booking-requests/templates"] });
  const submissionsQuery = useQuery<BookingRequestSubmission[]>({ queryKey: ["/api/app/booking-requests/submissions"] });
  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });

  const allProperties = propertiesQuery.data || [];
  const properties = allProperties.filter((p) => p.status === "active");
  const templates = templatesQuery.data || [];
  const submissions = submissionsQuery.data || [];

  const { toast } = useToast();
  const removeTemplate = useMutation({
    mutationFn: async (id: number) => apiRequest("DELETE", `/api/app/booking-requests/templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/booking-requests/templates"] });
      toast({ title: "Template deleted" });
    },
  });

  const duplicateTemplate = useMutation({
    mutationFn: async (id: number) => apiRequest("POST", `/api/app/booking-requests/templates/${id}/duplicate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/booking-requests/templates"] });
      toast({ title: "Template duplicated" });
    },
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiRequest("PATCH", `/api/app/booking-requests/templates/${id}`, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/app/booking-requests/templates"] }),
  });

  const copyPrimaryLink = async (templateId: number) => {
    try {
      const res = await fetch(`/api/app/booking-requests/templates/${templateId}/links`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load links");
      let links = (await res.json()) as Array<{ token: string; isActive?: boolean }>;
      let active = links.find((l) => l.isActive !== false);
      if (!active) {
        const created = await apiRequest("POST", `/api/app/booking-requests/templates/${templateId}/links`, { propertyId: null });
        const link = (await created.json()) as { token: string };
        active = link;
      }
      const url = `${window.location.origin}/booking-request/${active.token}`;
      await navigator.clipboard.writeText(url);
      toast({ title: "Public link copied", description: url });
    } catch (e: any) {
      toast({ title: "Error", description: parseErrorMessage(e, "Couldn't copy the public link. Please try again."), variant: "destructive" });
    }
  };

  const submissionCountByTemplate = useMemo(() => {
    const map: Record<number, number> = {};
    for (const s of submissions) map[s.templateId] = (map[s.templateId] || 0) + 1;
    return map;
  }, [submissions]);

  const [statusFilter, setStatusFilter] = useState<"all" | BookingRequestStatus>("all");
  const [templateFilter, setTemplateFilter] = useState<string>("all");
  // propertyFilter: array of property ids; sentinel 0 = "no property" (null propertyId)
  const [propertyFilter, setPropertyFilter] = useState<number[]>([]);
  // dateRangeFilter: rolling windows plus calendar presets (booking stats
  // build). Calendar presets resolve through the shared computeDateRange so
  // "last month" here and on the Stats tab agree on boundaries.
  type SubmissionsDateRange = "all" | "7d" | "30d" | "90d" | "last_month" | "last_quarter" | "last_year";
  const [dateRangeFilter, setDateRangeFilter] = useState<SubmissionsDateRange>("all");

  // Drill-through from the Stats tab charts: apply the equivalent filters on
  // the Submissions tab and switch over.
  const handleStatsDrillThrough = (f: StatsDrillThrough) => {
    if (f.status) setStatusFilter(f.status);
    if (f.propertyIds) setPropertyFilter(f.propertyIds);
    if (f.templateId !== undefined) setTemplateFilter(f.templateId == null ? "all" : String(f.templateId));
    // Carry the stats range too, or the drilled list won't match the chart
    // counts the user just clicked on.
    if (f.dateRange) setDateRangeFilter(f.dateRange);
    setTab("submissions");
  };

  // Sort state: column + direction
  type SortCol = "name" | "status" | "property" | "template" | "date";
  const [sortCol, setSortCol] = useState<SortCol>("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  function handleSort(col: SortCol) {
    if (sortCol === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir(col === "date" ? "desc" : "asc"); }
  }

  const filteredSubmissions = useMemo(() => {
    const now = Date.now();
    const dateRangeCutoff: number | null =
      dateRangeFilter === "7d" ? now - 7 * 24 * 60 * 60 * 1000 :
      dateRangeFilter === "30d" ? now - 30 * 24 * 60 * 60 * 1000 :
      dateRangeFilter === "90d" ? now - 90 * 24 * 60 * 60 * 1000 :
      null;
    // Calendar presets have both a start AND an end (e.g. last month ends at
    // the month boundary), unlike the rolling cutoffs above.
    const calendarRange =
      dateRangeFilter === "last_month" || dateRangeFilter === "last_quarter" || dateRangeFilter === "last_year"
        ? computeDateRange(dateRangeFilter)
        : null;
    const calendarFromMs = calendarRange ? new Date(`${calendarRange.from}T00:00:00`).getTime() : null;
    const calendarToMs = calendarRange ? new Date(`${calendarRange.to}T23:59:59.999`).getTime() : null;

    const filtered = submissions.filter((s) => {
      // Archived (applicant cancelled) stays out of the default view — pick the
      // explicit "Archived" filter to see them.
      if (statusFilter === "all" && s.status === "archived") return false;
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (templateFilter !== "all" && String(s.templateId) !== templateFilter) return false;
      // Property multi-select: empty = all; sentinel 0 = null propertyId.
      if (propertyFilter.length > 0) {
        const wantNone = propertyFilter.includes(0);
        if (s.propertyId == null) {
          if (!wantNone) return false;
        } else {
          if (!propertyFilter.includes(s.propertyId)) return false;
        }
      }
      // Date range filter
      if (dateRangeCutoff !== null && new Date(s.submittedAt).getTime() < dateRangeCutoff) return false;
      if (calendarFromMs !== null && calendarToMs !== null) {
        const t = new Date(s.submittedAt).getTime();
        if (t < calendarFromMs || t > calendarToMs) return false;
      }
      return true;
    });

    const tplNameOf = (id: number) => templates.find((t) => t.id === id)?.name ?? "";
    const propNameOf = (id: number | null) =>
      id == null ? "" : (properties.find((p) => p.id === id)?.name ?? properties.find((p) => p.id === id)?.address ?? "");

    filtered.sort((a, b) => {
      let cmp = 0;
      if (sortCol === "name") cmp = (a.submitterName || "").localeCompare(b.submitterName || "");
      else if (sortCol === "status") cmp = a.status.localeCompare(b.status);
      else if (sortCol === "property") cmp = propNameOf(a.propertyId ?? null).localeCompare(propNameOf(b.propertyId ?? null));
      else if (sortCol === "template") cmp = tplNameOf(a.templateId).localeCompare(tplNameOf(b.templateId));
      else cmp = new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime();
      return sortDir === "asc" ? cmp : -cmp;
    });
    return filtered;
  }, [submissions, statusFilter, templateFilter, propertyFilter, dateRangeFilter, sortCol, sortDir, templates, properties]);

  const statusBadgeColor = (s: string) => s === "approved" ? "bg-green-100 text-green-700" : s === "rejected" ? "bg-red-100 text-red-700" : s === "archived" ? "bg-gray-100 text-gray-500" : "bg-amber-100 text-amber-700";

  return (
    <AppLayout>
      <div className="p-6 max-w-6xl mx-auto">
        <ViewOnlyBanner area="members" />
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-page-title" data-tour="booking-requests-heading">Booking Requests</h1>
            <p className="text-sm text-gray-500 mt-1">Build branded prospect intake forms and review submissions.</p>
          </div>
          <div className="flex gap-2 items-center">
            <Link href="/app/members/booking-requests/settings">
              <Button variant="outline" data-testid="button-go-settings"><Settings className="h-4 w-4 mr-1" /> Branding</Button>
            </Link>
            <Button onClick={() => { setEditing(null); setEditorOpen(true); }} data-testid="button-new-template" data-tour="booking-requests-actions">
              <Plus className="h-4 w-4 mr-1" /> New template
            </Button>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="booking-requests" />
              <GuideButton pageKey="booking-requests" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="booking-requests" />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-tour="booking-requests-settings">
            <TabsTrigger value="submissions" data-testid="tab-submissions">Submissions ({submissions.length})</TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates">Templates ({templates.length})</TabsTrigger>
            <TabsTrigger value="stats" data-testid="tab-stats">Stats</TabsTrigger>
          </TabsList>

          <TabsContent value="submissions" className="space-y-2">
            {submissions.length > 0 && (
              <div className="flex flex-wrap gap-2 items-center pb-2">
                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as "all" | BookingRequestStatus)}>
                  <SelectTrigger className="h-9 w-auto min-w-[160px] text-sm" data-testid="select-status-filter">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={templateFilter} onValueChange={setTemplateFilter}>
                  <SelectTrigger className="h-9 w-auto min-w-[180px] text-sm" data-testid="select-template-filter">
                    <SelectValue placeholder="All templates" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All templates</SelectItem>
                    {templates.map((t) => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <PropertyMultiSelectFilter
                  properties={properties as any}
                  value={propertyFilter}
                  onChange={setPropertyFilter}
                  className="min-w-[180px]"
                  noPropertyOption
                />
                <Select value={dateRangeFilter} onValueChange={(v) => setDateRangeFilter(v as SubmissionsDateRange)}>
                  <SelectTrigger className="h-9 w-auto min-w-[150px] text-sm" data-testid="select-date-range-filter">
                    <SelectValue placeholder="All time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All time</SelectItem>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                    <SelectItem value="90d">Last 90 days</SelectItem>
                    <SelectItem value="last_month">Last month</SelectItem>
                    <SelectItem value="last_quarter">Last quarter</SelectItem>
                    <SelectItem value="last_year">Last year</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground" data-testid="text-filter-count">Showing {filteredSubmissions.length} of {submissions.length}</span>
              </div>
            )}
            {filteredSubmissions.length === 0 ? (
              <Card className="p-8 text-center text-sm text-gray-500">{submissions.length === 0 ? "No submissions yet. Create a template and share its link with prospects." : "No submissions match these filters."}</Card>
            ) : (
              <div className="rounded-md border overflow-x-auto" data-testid="table-submissions">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      {(
                        [
                          { col: "name" as const, label: "Submitter Name" },
                          { col: "status" as const, label: "Status" },
                          { col: "property" as const, label: "Property" },
                          { col: "template" as const, label: "Template" },
                          { col: "date" as const, label: "Date Submitted" },
                        ] as const
                      ).map(({ col, label }) => (
                        <th
                          key={col}
                          className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap hover:text-gray-900"
                          onClick={() => handleSort(col)}
                          data-testid={`th-${col}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {label}
                            {sortCol === col ? (
                              sortDir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />
                            ) : (
                              <ArrowUpDown className="h-3.5 w-3.5 opacity-30" />
                            )}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubmissions.map((s) => {
                      const tpl = templates.find((t) => t.id === s.templateId);
                      const matchedProperty = s.propertyId != null ? properties.find((p) => p.id === s.propertyId) : null;
                      const propertyName = matchedProperty ? (matchedProperty.name || matchedProperty.address || `Property ${matchedProperty.id}`) : (s.propertyId == null ? "—" : `Property ${s.propertyId}`);
                      return (
                        <tr
                          key={s.id}
                          className="border-b last:border-0 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => setOpenSubmission(s.id)}
                          data-testid={`row-submission-${s.id}`}
                        >
                          <td className="px-4 py-3 font-medium">{s.submitterName || "Anonymous"}</td>
                          <td className="px-4 py-3">
                            <Badge className={statusBadgeColor(s.status)} data-testid={`badge-status-${s.id}`}>{s.status}</Badge>
                          </td>
                          <td className="px-4 py-3 text-gray-600 max-w-[180px] truncate" title={matchedProperty?.name || undefined}>{propertyName}</td>
                          <td className="px-4 py-3 text-gray-600 max-w-[160px] truncate" title={tpl?.name}>{tpl?.name || "—"}</td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{new Date(s.submittedAt).toLocaleDateString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="templates" className="space-y-2">
            {templates.length === 0 ? (
              <Card className="p-8 text-center text-sm text-gray-500">
                No templates yet. <button className="text-blue-600 underline" onClick={() => { setEditing(null); setEditorOpen(true); }}>Create one</button>.
              </Card>
            ) : (
              templates.map((t) => {
                const subCount = submissionCountByTemplate[t.id] || 0;
                const fieldCount = (t.fields as BookingRequestField[])?.length || 0;
                const propCount = ((t.propertyIds as number[]) || []).length;
                return (
                  <Card key={t.id} className="p-4 flex items-center justify-between gap-3" data-testid={`row-template-${t.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{t.name}</span>
                        {!t.isActive && <Badge variant="secondary" data-testid={`badge-inactive-${t.id}`}>Inactive</Badge>}
                        {t.createdByName && (
                          <Badge variant="outline" className="font-normal" data-testid={`badge-created-by-${t.id}`}>
                            <User className="h-3 w-3 mr-1" />Created by {t.createdByName}
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-2">
                        <span data-testid={`text-field-count-${t.id}`}>{fieldCount} field{fieldCount === 1 ? "" : "s"}</span>
                        <span>·</span>
                        <span>{propCount === 0 ? "all properties" : `${propCount} propert${propCount === 1 ? "y" : "ies"}`}</span>
                        <span>·</span>
                        <span data-testid={`text-submission-count-${t.id}`}>{subCount} submission{subCount === 1 ? "" : "s"}</span>
                        <span>·</span>
                        <span data-testid={`text-created-${t.id}`}>created {new Date(t.createdAt).toLocaleDateString()}</span>
                      </div>
                      {t.isStarterTemplate && (
                        <p className="text-xs text-brand mt-1" data-testid={`text-starter-note-${t.id}`}>
                          This is your PadPulse starter template. Customize it or create additional templates as needed.
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <label className="flex items-center gap-1.5 text-xs text-gray-600">
                        <Switch checked={t.isActive} onCheckedChange={(v) => toggleActive.mutate({ id: t.id, isActive: v })} data-testid={`switch-active-${t.id}`} />
                        <span>Active</span>
                      </label>
                      <Button variant="ghost" size="icon" onClick={() => copyPrimaryLink(t.id)} title="Copy public link" data-testid={`button-copy-link-${t.id}`}><Copy className="h-4 w-4" /></Button>
                      <Button variant="outline" size="sm" onClick={() => { setPreviewTemplate(t); setPreviewOpen(true); }} data-testid={`button-template-preview-${t.id}`}><Eye className="h-4 w-4 mr-1" /> Preview</Button>
                      <Button variant="outline" size="sm" onClick={() => { setEditing(t); setEditorOpen(true); }} data-testid={`button-edit-template-${t.id}`}>Edit</Button>
                      <Button variant="ghost" size="icon" onClick={() => duplicateTemplate.mutate(t.id)} title="Duplicate" data-testid={`button-duplicate-template-${t.id}`}><Files className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => { if (confirm(`Delete "${t.name}"? Its share links stop working immediately. Past submissions are kept and stay visible in Submissions and Stats.`)) removeTemplate.mutate(t.id); }} data-testid={`button-delete-template-${t.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </Card>
                );
              })
            )}
          </TabsContent>

          <TabsContent value="stats" className="space-y-2">
            <BookingRequestsStatsTab
              templates={templates}
              properties={properties}
              onDrillThrough={handleStatsDrillThrough}
            />
          </TabsContent>
        </Tabs>
      </div>

      <TemplateEditor open={editorOpen} onOpenChange={setEditorOpen} template={editing} />
      <PreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} template={previewTemplate} />
      {/* key forces a remount when the id changes so the seeded review
          notes/category can never carry over from another submission. */}
      {openSubmission !== null && <SubmissionDetail key={openSubmission} id={openSubmission} properties={properties} onClose={() => setOpenSubmission(null)} />}
    </AppLayout>
  );
}
